/**
 * Session API Service
 * Handles all session-related API calls to the backend
 */

import { SessionStatus, MealPlanningSessionRecord } from '../types/session'
import { BaseApiService } from './BaseApiService'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3005'

class SessionApiService extends BaseApiService {
  constructor() {
    super({
      baseUrl: API_BASE_URL,
      defaultTimeout: 15000,
      defaultRetries: 3,
      enableRetry: true
    })
  }

  /**
   * Create a new meal planning session
   */
  async createSession(userId: string, persistentPreferences: any, sessionPreferences: any): Promise<any> {
    return this.post('/api/sessions', {
      userId,
      persistentPreferences,
      sessionPreferences
    })
  }

  /**
   * Get session by ID
   */
  async getSession(sessionId: string, userId?: string): Promise<any> {
    const endpoint = userId
      ? `/api/sessions/${sessionId}?userId=${userId}`
      : `/api/sessions/${sessionId}`

    try {
      return await this.get(endpoint)
    } catch (error: any) {
      if (error.message.includes('404')) {
        return null
      }
      throw error
    }
  }

  /**
   * Get active (non-complete) session for user
   */
  async getActiveSession(userId: string): Promise<MealPlanningSessionRecord | null> {
    try {
      return await this.get(`/api/users/${userId}/sessions/active`)
    } catch (error: any) {
      if (error.message.includes('404')) {
        return null
      }
      throw error
    }
  }

  /**
   * Update session status
   */
  async updateStatus(sessionId: string, userId: string, status: SessionStatus): Promise<MealPlanningSessionRecord> {
    return this.put(`/api/sessions/${sessionId}/status`, { userId, status })
  }

  /**
   * Mark session as complete
   */
  async markComplete(sessionId: string, userId: string): Promise<MealPlanningSessionRecord> {
    return this.post(`/api/sessions/${sessionId}/complete`, { userId })
  }

  /**
   * Save meals to session
   */
  async saveMeals(sessionId: string, userId: string, meals: any[]): Promise<MealPlanningSessionRecord> {
    return this.put(`/api/sessions/${sessionId}/meals`, { userId, meals })
  }

  /**
   * Update session preferences
   */
  async updatePreferences(
    sessionId: string,
    userId: string,
    persistentPreferences?: any,
    sessionPreferences?: any
  ): Promise<any> {
    return this.put(`/api/sessions/${sessionId}`, {
      userId,
      persistentPreferences,
      sessionPreferences
    })
  }

  /**
   * localStorage helpers for active session tracking
   */
  setActiveSessionId(sessionId: string): void {
    localStorage.setItem('activeSessionId', sessionId)
  }

  getActiveSessionId(): string | null {
    return localStorage.getItem('activeSessionId')
  }

  clearActiveSessionId(): void {
    localStorage.removeItem('activeSessionId')
  }
}

export const sessionApiService = new SessionApiService()
export default sessionApiService
